import { useEffect } from "react";
import { Link, useParams } from "wouter";
import { scrollToPageTop } from "@/components/HelioLogoMark";
import ReactMarkdown from "react-markdown";
import Seo from "@/components/Seo";
import ArticleJsonLd from "@/components/Seo/ArticleJsonLd";
import PowloLayout from "@/components/powlo/PowloLayout";
import TagChip from "@/components/Blog/TagChip";
import ShareButtons from "@/components/Blog/ShareButtons";
import RelatedPosts from "@/components/Blog/RelatedPosts";
import { useBlogPost, useBlogPosts } from "@/api/hooks";
import { formatUkDate } from "@/utils/date";
import { SITE_URL } from "@/const";

export default function BlogPost() {
  const params = useParams<{ slug: string }>();
  const slug = params.slug;

  useEffect(() => {
    scrollToPageTop();
  }, [slug]);
  const { data: post, isLoading, isError, error, refetch } = useBlogPost(slug);
  const notFound = (error as { status?: number } | undefined)?.status === 404;
  const primaryTag = post?.tags[0];
  const relatedQuery = useBlogPosts({ tag: primaryTag });
  const related = (relatedQuery.data?.data ?? [])
    .filter((item) => item.slug !== slug)
    .slice(0, 3);

  const canonical = `/blog/${slug ?? ""}`;
  const pageUrl = `${SITE_URL}${canonical}`;

  return (
    <>
      {post ? (
        <>
          <Seo
            title={post.og_title || post.title}
            description={post.og_description || post.excerpt || post.title}
            image={post.og_image_url || post.hero_image_url || undefined}
            path={canonical}
            ogType="article"
          />
          <ArticleJsonLd
            headline={post.title}
            datePublished={post.published_at}
            author={post.author_name}
            image={post.og_image_url || post.hero_image_url}
            description={post.og_description || post.excerpt}
            url={pageUrl}
          />
        </>
      ) : (
        <Seo title={notFound ? "Post not found" : "Blog"} description="Powlo blog" path={canonical} />
      )}
      <PowloLayout>
        <div className="max-w-[42rem] mx-auto px-5 sm:px-6 pt-32 sm:pt-[140px] pb-24 sm:pb-28">
          {isLoading ? <p className="text-[#666666]">Loading…</p> : null}

          {notFound ? (
            <div className="text-center py-16">
              <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mb-4 text-brand-navy">
                Post not found
              </h1>
              <p className="text-[#666666] leading-relaxed mb-8">
                This article may have been unpublished or the link is incorrect.
              </p>
              <Link href="/blog" className="text-[#0E9E77] hover:underline underline-offset-4">
                Return to the blog index
              </Link>
            </div>
          ) : null}

          {isError && !notFound ? (
            <div className="text-center py-16">
              <p className="text-[#333333] leading-relaxed mb-6">We couldn&apos;t load this post.</p>
              <button
                type="button"
                onClick={() => void refetch()}
                className="btn-cta rounded-full font-medium px-5 py-2.5 text-sm"
              >
                Try again
              </button>
            </div>
          ) : null}

          {post ? (
            <article>
              <header className="mb-10 sm:mb-12">
                <p className="text-[13px] tracking-[0.14em] uppercase text-[#666666] mb-5">Insights</p>
                <h1 className="font-display text-[2.15rem] sm:text-5xl font-medium tracking-[-0.01em] leading-[1.12] mb-5 text-brand-navy">
                  {post.title}
                </h1>
                {post.subtitle ? (
                  <p className="text-xl sm:text-2xl text-[#666666] font-light leading-snug mb-7">
                    {post.subtitle}
                  </p>
                ) : null}
                <p className="text-sm text-[#666666] tracking-wide mb-6">
                  {post.author_name}
                  {post.published_at ? ` · ${formatUkDate(post.published_at)}` : ""}
                  {post.reading_time_minutes ? ` · ${post.reading_time_minutes} min read` : ""}
                </p>
                {post.tags.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag) => (
                      <TagChip key={tag} tag={tag} />
                    ))}
                  </div>
                ) : null}
              </header>
              {post.hero_image_url ? (
                <img
                  src={post.hero_image_url}
                  alt=""
                  className="w-full rounded-[28px] mb-10 sm:mb-12 aspect-[16/9] object-cover"
                />
              ) : null}
              {post.render_mode === "html" || post.body_html ? (
                <div
                  className="blog-prose"
                  dangerouslySetInnerHTML={{ __html: post.body_html || "" }}
                />
              ) : (
                <div className="blog-prose">
                  <ReactMarkdown>{post.body_md}</ReactMarkdown>
                </div>
              )}
              <div className="mt-14 pt-8 border-t border-[rgba(17,17,17,0.08)] flex items-center justify-between gap-4 flex-wrap">
                <ShareButtons url={pageUrl} title={post.title} />
                <Link href="/blog" className="text-sm text-[#666666] hover:text-[#0E9E77] transition-colors">
                  More from the blog
                </Link>
              </div>
              <RelatedPosts posts={related} />
            </article>
          ) : null}
        </div>
      </PowloLayout>
    </>
  );
}
